import type { CoachRecommendedActionFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedActionFragment';
import type { CoachRecommendedEditFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedEditFragment';
import type { CoachRecommendedProductCardsFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedProductCardsFragment';
import type { CourseraContextFragment } from 'bundles/ai-coach-platform/queries/__generated__/courseraContextFragment';
import type { ItemNavigationButtonFragment } from 'bundles/ai-coach-platform/queries/__generated__/navigationButtonFragment';
import type { CoachActionType } from 'bundles/ai-coach-platform/types';

import type { Feedback, StructuredResponse } from '../types';
import * as actionsTypes from './types';

export function toggleChat(): actionsTypes.ToggleChat {
  return {
    type: actionsTypes.TOGGLE_CHAT,
  };
}

export function openCoachWidget(): actionsTypes.OpenCoachWidget {
  return {
    type: actionsTypes.OPEN_COACH_WIDGET,
  };
}

export function closeCoachWidget(): actionsTypes.CloseCoachWidget {
  return {
    type: actionsTypes.CLOSE_COACH_WIDGET,
  };
}

export function toggleInputDisabled(): actionsTypes.ToggleInputDisabled {
  return {
    type: actionsTypes.TOGGLE_INPUT_DISABLED,
  };
}

export function updateChatHistory(
  messages: actionsTypes.UpdateChatHistory['messages']
): actionsTypes.UpdateChatHistory {
  return {
    type: actionsTypes.UPDATE_CHAT_HISTORY,
    messages,
  };
}

export function prependChatHistory(
  messages: actionsTypes.PrependChatHistory['messages']
): actionsTypes.PrependChatHistory {
  return {
    type: actionsTypes.PREPEND_CHAT_HISTORY,
    messages,
  };
}

export function addUserMessage(
  text: string,
  id?: string,
  timestamp?: number,
  action?: CoachActionType,
  courseraContext?: CourseraContextFragment | null
): actionsTypes.AddUserMessage {
  return {
    type: actionsTypes.ADD_NEW_USER_MESSAGE,
    text,
    id,
    timestamp,
    action,
    courseraContext,
  };
}

export function addResponseMessage(
  text: string,
  id?: string,
  userFeedback?: Feedback | null,
  timestamp?: number,
  navigationButtons?: ItemNavigationButtonFragment[] | null,
  structuredResponse?: StructuredResponse,
  recommendedActions?: CoachRecommendedActionFragment[],
  courseraContext?: CourseraContextFragment | null,
  recommendedEdits?: CoachRecommendedEditFragment[] | null,
  productCards?: CoachRecommendedProductCardsFragment[] | null
): actionsTypes.AddResponseMessage {
  return {
    type: actionsTypes.ADD_NEW_RESPONSE_MESSAGE,
    text,
    id,
    userFeedback,
    timestamp,
    navigationButtons,
    structuredResponse,
    recommendedActions,
    courseraContext,
    recommendedEdits,
    productCards,
  };
}

export function addStreamedResponseMessage(text: string | null): actionsTypes.AddStreamedResponseMessage {
  return {
    type: actionsTypes.ADD_STREAMED_RESPONSE_MESSAGE,
    text,
  };
}

export function enableMsgLoader(enabledState: boolean): actionsTypes.EnableMsgLoader {
  return {
    type: actionsTypes.ENABLE_MESSAGE_LOADER,
    messageLoader: enabledState,
  };
}

export function setIsMessageStreaming(isMessageStreaming: boolean): actionsTypes.IsMessageStreaming {
  return {
    type: actionsTypes.IS_MESSAGE_STREAMING,
    isMessageStreaming,
  };
}

export function dropMessages(): actionsTypes.DropMessages {
  return {
    type: actionsTypes.DROP_MESSAGES,
  };
}

export function hideAvatar(index: number): actionsTypes.HideAvatar {
  return {
    type: actionsTypes.HIDE_AVATAR,
    index,
  };
}

export function deleteMessages(count: number, id?: string): actionsTypes.DeleteMessages {
  return {
    type: actionsTypes.DELETE_MESSAGES,
    count,
    id,
  };
}

export function setUserInput(inputText: string): actionsTypes.SetUserInputText {
  return {
    type: actionsTypes.SET_USER_INPUT_TEXT,
    inputText,
  };
}

export function setLike(id?: string): actionsTypes.FeedbackSetLike {
  return {
    type: actionsTypes.FEEDBACK_SET_LIKE,
    id,
  };
}

export function setUnLike(id?: string): actionsTypes.FeedbackSetDisLike {
  return {
    type: actionsTypes.FEEDBACK_SET_UNLIKE,
    id,
  };
}

export function setUnSet(id?: string): actionsTypes.FeedbackUnSet {
  return {
    type: actionsTypes.FEEDBACK_UNSET,
    id,
  };
}

export function setChatSessionId(chatSessionId?: string): actionsTypes.SetChatSessionId {
  return {
    type: actionsTypes.SET_CHAT_SESSION_ID,
    chatSessionId,
  };
}

export function setChatSessionStorageKey(chatSessionStorageKey?: string): actionsTypes.SetChatSessionStorageKey {
  return {
    type: actionsTypes.SET_CHAT_SESSION_STORAGE_KEY,
    chatSessionStorageKey,
  };
}

export function setContinuousChatSessionId(continuousChatSessionId?: string): actionsTypes.SetContinuousChatSessionId {
  return {
    type: actionsTypes.SET_CONTINUOUS_CHAT_SESSION_ID,
    continuousChatSessionId,
  };
}

export function setContinuousChatSessionStorageKey(
  continuousChatSessionStorageKey?: string
): actionsTypes.SetContinuousChatSessionStorageKey {
  return {
    type: actionsTypes.SET_CONTINUOUS_CHAT_SESSION_STORAGE_KEY,
    continuousChatSessionStorageKey,
  };
}

export function setPersonalizedWelcomeChatSessionId(
  personalizedWelcomeChatSessionId?: string
): actionsTypes.SetPersonalizedWelcomeChatSessionId {
  return {
    type: actionsTypes.SET_PERSONALIZED_WELCOME_CHAT_SESSION_ID,
    personalizedWelcomeChatSessionId,
  };
}

export function setPersonalizedWelcomeChatSessionStorageKey(
  personalizedWelcomeChatSessionStorageKey?: string
): actionsTypes.SetPersonalizedWelcomeChatSessionStorageKey {
  return {
    type: actionsTypes.SET_PERSONALIZED_WELCOME_CHAT_SESSION_STORAGE_KEY,
    personalizedWelcomeChatSessionStorageKey,
  };
}

export function clearChatSession(): actionsTypes.ClearChatSession {
  return {
    type: actionsTypes.CLEAR_CHAT_SESSION,
  };
}

export function clearContinuousChatSession(): actionsTypes.ClearContinuousChatSession {
  return {
    type: actionsTypes.CLEAR_CONTINUOUS_CHAT_SESSION,
  };
}

export function clearPersonalizedWelcomeSession(): actionsTypes.ClearPersonalizedWelcomeSession {
  return {
    type: actionsTypes.CLEAR_PERSONALIZED_WELCOME_SESSION,
  };
}
